/**
 * Error Logger Utility
 * Centralized error logging and handling
 */

export interface ErrorLogEntry {
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  component?: string;
  error?: Error;
  context?: Record<string, any>;
}

class ErrorLogger {
  private logs: ErrorLogEntry[] = [];
  private maxLogs = 1000;

  log(level: ErrorLogEntry['level'], message: string, options: {
    component?: string;
    error?: Error;
    context?: Record<string, any>;
  } = {}) {
    const entry: ErrorLogEntry = {
      timestamp: new Date(),
      level,
      message,
      ...options
    };

    this.logs.push(entry);

    // Keep logs under max size
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output
    const prefix = `[${entry.timestamp.toISOString()}]`;
    const componentPrefix = entry.component ? `[${entry.component}]` : '';
    
    switch (level) {
      case 'error':
        console.error(`${prefix} ERROR ${componentPrefix} ${message}`, entry.error || '');
        break;
      case 'warn':
        console.warn(`${prefix} WARN ${componentPrefix} ${message}`);
        break;
      case 'info':
        console.info(`${prefix} INFO ${componentPrefix} ${message}`);
        break;
      case 'debug':
        console.debug(`${prefix} DEBUG ${componentPrefix} ${message}`);
        break;
    }
  }

  info(message: string, options?: { component?: string; context?: Record<string, any> }) {
    this.log('info', message, options);
  }

  warn(message: string, options?: { component?: string; context?: Record<string, any> }) {
    this.log('warn', message, options);
  }

  error(message: string, options?: { component?: string; error?: Error; context?: Record<string, any> }) {
    this.log('error', message, options);
  }

  debug(message: string, options?: { component?: string; context?: Record<string, any> }) {
    this.log('debug', message, options);
  }

  getLogs(level?: ErrorLogEntry['level']) {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }
}

export const errorLogger = new ErrorLogger(); 